import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { Avatar, Icon, PageBody, Pill, Skeleton, SkeletonText, useToast } from "@astrum/ui";
import type { PillTone } from "@astrum/ui";
import { api, ApiError } from "../lib/api.js";
import type { Channel, Project, Task } from "../lib/api.js";
import { useAuth } from "../lib/auth.js";
import { usePageTitle } from "../lib/usePageTitle.js";
import "./home.css";

/**
 * The landing surface, built to `design-mockups/01-shell-home.html`.
 *
 * A stat strip, a single highlighted focus, project health, then a two-column
 * grid of today's work, the day's schedule, and what the team has been doing.
 * Every section renders whether or not it has content — a layout that
 * rearranges itself depending on the day is one nobody learns the shape of,
 * and an empty section is information rather than something to hide.
 */

type Event = {
  id: string;
  title: string;
  startsAt: string;
  endsAt: string;
  location: string | null;
  allDay: boolean;
};

type Post = {
  id: string;
  body: string;
  authorName: string;
  authorAvatarUrl: string | null;
  createdAt: string;
  commentCount: number;
};

const PRIORITY_TONE: Record<string, PillTone> = {
  low: "neutral",
  normal: "neutral",
  medium: "warn",
  high: "danger",
  urgent: "danger",
};

const HEALTH_TONE: Record<string, PillTone> = {
  on_track: "ok",
  at_risk: "warn",
  off_track: "danger",
};

function greeting(name: string): string {
  const hour = new Date().getHours();
  const part = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";
  return `${part}, ${name.split(" ")[0]}`;
}

function longDate(): string {
  return new Date().toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" });
}

function describeDue(iso: string | null, now: number): { text: string; tone: "overdue" | "soon" | "later" } | null {
  if (!iso) return null;
  const days = Math.ceil((new Date(iso).getTime() - now) / 86_400_000);
  if (days < 0) return { text: days === -1 ? "Yesterday" : `${Math.abs(days)}d ago`, tone: "overdue" };
  if (days === 0) return { text: "Today", tone: "overdue" };
  if (days === 1) return { text: "Tomorrow", tone: "soon" };
  if (days <= 7) return { text: `In ${days}d`, tone: "soon" };
  return { text: new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" }), tone: "later" };
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

function relativeTime(iso: string, now: number): string {
  const diff = now - new Date(iso).getTime();
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

/* ─────────────────────────────── pieces ─────────────────────────────── */

function StatCard({
  value,
  label,
  tone,
  to,
}: {
  value: number;
  label: string;
  tone?: "danger" | "warn";
  to: string;
}) {
  return (
    <Link to={to} className={`home-stat ${tone ? `is-${tone}` : ""}`}>
      <span className="home-stat__value">{value}</span>
      <span className="home-stat__label">{label}</span>
    </Link>
  );
}

function TaskRow({ task, now }: { task: Task; now: number }) {
  const due = describeDue(task.dueAt, now);
  return (
    <li>
      <Link to={`/work/task/${task.id}`} className="home-task">
        <span className="home-task__check" aria-hidden="true" />
        <span className="home-task__title">{task.title}</span>
        {task.projectCode && <span className="home-task__project">{task.projectCode}</span>}
        <Pill tone={PRIORITY_TONE[task.priority] ?? "neutral"}>{task.priority}</Pill>
        {due && <span className={`home-task__due is-${due.tone}`}>{due.text}</span>}
      </Link>
    </li>
  );
}

function SectionEmpty({ icon, text }: { icon: "check" | "calendar" | "chat" | "work"; text: string }) {
  return (
    <div className="home-empty">
      <Icon name={icon} size={16} />
      <span>{text}</span>
    </div>
  );
}

/**
 * A prominent, dismissible reminder shown on Home when the viewer still owes
 * today's check-in report — the persistent counterpart to the single 4:30pm
 * notification. Dismissal is remembered for the day (keyed by the local date
 * the server returns), so it reappears tomorrow rather than staying hidden.
 */
function CheckinBanner() {
  const [status, setStatus] = useState<{ owesReport: boolean; today: string } | null>(null);
  const [dismissedDate, setDismissedDate] = useState<string | null>(() =>
    typeof localStorage !== "undefined" ? localStorage.getItem("astrum.checkin.dismissed") : null,
  );

  useEffect(() => {
    let cancelled = false;
    api
      .get<{ owesReport: boolean; today: string }>("/me/checkin-status")
      .then((res) => {
        if (!cancelled) setStatus(res);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  if (!status?.owesReport || dismissedDate === status.today) return null;

  const dismiss = () => {
    localStorage.setItem("astrum.checkin.dismissed", status.today);
    setDismissedDate(status.today);
  };

  return (
    <div className="home__checkin-banner" role="status">
      <Icon name="clock" size={18} />
      <div className="home__checkin-text">
        <strong>You haven&rsquo;t filed today&rsquo;s report.</strong> Add it before you sign off.
      </div>
      <Link to="/daily-report" className="home__checkin-cta">
        File report
      </Link>
      <button type="button" className="home__checkin-dismiss" onClick={dismiss} aria-label="Dismiss reminder">
        <Icon name="close" size={14} />
      </button>
    </div>
  );
}

/* ─────────────────────────────── main view ─────────────────────────────── */

export function Home() {
  usePageTitle("Home");
  const { viewer } = useAuth();
  const { toast } = useToast();

  const [tasks, setTasks] = useState<Task[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [channels, setChannels] = useState<Channel[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  /* Captured at fetch rather than read during render: a Date.now() inside a
   * memo makes the buckets depend on when React happens to re-render, and they
   * would never refresh on their own. */
  const [now, setNow] = useState(() => Date.now());

  const load = useCallback(() => {
    void (async () => {
      setLoading(true);

      const dayStart = new Date();
      dayStart.setHours(0, 0, 0, 0);
      const dayEnd = new Date(dayStart.getTime() + 86_400_000);

      // One parallel batch: this is a landing page, and five chained round trips
      // is the difference between instant and sluggish.
      try {
        const [taskRes, projectRes, channelRes, eventRes, postRes] = await Promise.all([
          api.get<{ tasks: Task[] }>("/tasks?assignee=me&status=todo,in_progress,review&limit=100"),
          api.get<{ projects: Project[] }>("/projects"),
          api.get<{ channels: Channel[] }>("/channels"),
          api.get<{ events: Event[] }>(`/events?from=${dayStart.toISOString()}&to=${dayEnd.toISOString()}`),
          api.get<{ posts: Post[] }>("/posts?limit=5"),
        ]);
        setTasks(taskRes.tasks);
        setProjects(projectRes.projects);
        setChannels(channelRes.channels);
        setEvents(eventRes.events);
        setPosts(postRes.posts);
      } catch (error) {
        toast(error instanceof ApiError ? error.message : "Could not load your overview.", { tone: "danger" });
      } finally {
        setNow(Date.now());
        setLoading(false);
      }
    })();
  }, [toast]);

  useEffect(() => load(), [load]);

  const overdue = useMemo(
    () => tasks.filter((t) => t.dueAt && new Date(t.dueAt).getTime() < now),
    [tasks, now],
  );

  const dueThisWeek = useMemo(() => {
    const horizon = now + 7 * 86_400_000;
    return tasks.filter(
      (t) => t.dueAt && new Date(t.dueAt).getTime() >= now && new Date(t.dueAt).getTime() <= horizon,
    );
  }, [tasks, now]);

  const unreadTotal = useMemo(
    () => channels.reduce((sum, c) => sum + (c.unreadCount ?? 0), 0),
    [channels],
  );

  const unreadChannels = useMemo(() => channels.filter((c) => (c.unreadCount ?? 0) > 0), [channels]);

  /* The single most pressing thing, chosen rather than listed: the most
   * overdue task, or failing that the nearest deadline. A "focus" that shows
   * five things is just another list. */
  const focus = useMemo(() => {
    const byDue = [...overdue, ...dueThisWeek].sort(
      (a, b) => new Date(a.dueAt!).getTime() - new Date(b.dueAt!).getTime(),
    );
    return byDue[0];
  }, [overdue, dueThisWeek]);

  const todaysTasks = useMemo(() => {
    const ranked = [...overdue, ...dueThisWeek];
    // Everything else assigned, so the list is never thin on a calm day.
    const flagged = new Set(ranked.map((t) => t.id));
    return [...ranked, ...tasks.filter((t) => !flagged.has(t.id))].slice(0, 8);
  }, [overdue, dueThisWeek, tasks]);

  const myProjects = useMemo(() => projects.filter((p) => p.isMember).slice(0, 4), [projects]);

  if (loading) {
    return (
      <PageBody
        className="home"
        width="wide"
        eyebrow={<Skeleton height={14} width={140} />}
        title={<Skeleton height={30} width={300} />}
      >
          <div className="home__stats">
            {Array.from({ length: 4 }).map((_, i) => (
              <div className="home-stat" key={i}>
                <SkeletonText lines={2} />
              </div>
            ))}
          </div>
          <div className="home__grid">
            <section className="home-card">
              <SkeletonText lines={6} />
            </section>
            <section className="home-card">
              <SkeletonText lines={5} />
            </section>
          </div>
      </PageBody>
    );
  }

  const focusDue = focus ? describeDue(focus.dueAt, now) : null;

  return (
    <PageBody
      className="home"
      width="wide"
      eyebrow={longDate()}
      title={greeting(viewer?.displayName ?? "there")}
    >

        <CheckinBanner />

        {/* The day in four numbers, each a way into the thing it counts. */}
        <div className="home__stats">
          <StatCard value={tasks.length} label="Assigned to you" to="/work?mine=1" />
          <StatCard value={overdue.length} label="Overdue" tone={overdue.length ? "danger" : undefined} to="/work?mine=1" />
          <StatCard value={dueThisWeek.length} label="Due this week" tone={dueThisWeek.length ? "warn" : undefined} to="/work?mine=1" />
          <StatCard value={unreadTotal} label="Unread messages" to="/chat" />
        </div>

        {focus && (
          <section className="home-focus">
            <div className="home-focus__body">
              <span className="home-focus__eyebrow">
                {focusDue?.tone === "overdue" ? "Most overdue" : "Next deadline"}
              </span>
              <h2>{focus.title}</h2>
              <p>
                {focus.projectName ?? "No project"}
                {focusDue ? ` · due ${focusDue.text.toLowerCase()}` : ""}
              </p>
            </div>
            {/* A Link styled as the button rather than a Button that assigns
                location: a full page load here would discard the app state and
                re-fetch everything. */}
            <Link to={`/work/task/${focus.id}`} className="home-focus__action">
              Open task
              <Icon name="arrow-right" size={14} />
            </Link>
          </section>
        )}

        {myProjects.length > 0 && (
          <section className="home__section">
            <div className="home__section-head">
              <h2>Your projects</h2>
              <Link to="/work" className="home__section-link">
                All work
                <Icon name="arrow-right" size={12} />
              </Link>
            </div>

            <div className="home__projects">
              {myProjects.map((project) => {
                const total = project.taskCount ?? 0;
                const complete = total > 0 ? Math.round(((project.doneTaskCount ?? 0) / total) * 100) : 0;
                return (
                  <Link key={project.id} to={`/work/project/${project.id}`} className="home-project">
                    <div className="home-project__top">
                      <span className="home-project__code">{project.code}</span>
                      <Pill tone={HEALTH_TONE[project.health] ?? "neutral"} dot>
                        {project.health.replace("_", " ")}
                      </Pill>
                    </div>
                    <span className="home-project__name">{project.name}</span>
                    <div className="home-project__bar" aria-label={`${complete}% complete`}>
                      <span style={{ width: `${complete}%` }} />
                    </div>
                    <div className="home-project__foot">
                      <span>{project.openTaskCount ?? 0} open</span>
                      <span className="home-project__pct">{complete}%</span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>
        )}

        <div className="home__grid">
          <section className="home-card">
            <div className="home-card__head">
              <h2>Your tasks</h2>
              <Link to="/work?mine=1" className="home__section-link">
                See all
                <Icon name="arrow-right" size={12} />
              </Link>
            </div>
            {todaysTasks.length === 0 ? (
              <SectionEmpty icon="check" text="Nothing assigned to you right now." />
            ) : (
              <ul className="home__list">
                {todaysTasks.map((task) => (
                  <TaskRow key={task.id} task={task} now={now} />
                ))}
              </ul>
            )}
          </section>

          <div className="home__column">
            <section className="home-card">
              <div className="home-card__head">
                <h2>Today</h2>
                <Link to="/schedule" className="home__section-link">
                  Schedule
                  <Icon name="arrow-right" size={12} />
                </Link>
              </div>
              {events.length === 0 ? (
                <SectionEmpty icon="calendar" text="Nothing in the diary today." />
              ) : (
                <ul className="home__list">
                  {events.map((event) => (
                    <li key={event.id}>
                      <Link to="/schedule" className="home-agenda">
                        <span className="home-agenda__time">
                          {event.allDay ? "All day" : formatTime(event.startsAt)}
                        </span>
                        <span className="home-agenda__dot" aria-hidden="true" />
                        <span className="home-agenda__body">
                          <strong>{event.title}</strong>
                          {event.location && <span>{event.location}</span>}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className="home-card">
              <div className="home-card__head">
                <h2>Unread</h2>
                <Link to="/chat" className="home__section-link">
                  Chat
                  <Icon name="arrow-right" size={12} />
                </Link>
              </div>
              {unreadChannels.length === 0 ? (
                <SectionEmpty icon="chat" text="You are caught up." />
              ) : (
                <ul className="home__list">
                  {unreadChannels.slice(0, 5).map((channel) => (
                    <li key={channel.id}>
                      <Link to={`/chat/${channel.id}`} className="home-channel">
                        <span className="home-channel__hash" aria-hidden="true">
                          #
                        </span>
                        <span className="home-channel__name">{channel.name}</span>
                        <span className="home-channel__count">{channel.unreadCount}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        </div>

        {/*
         * The same card as Today and Unread beside it.
         *
         * This one had its heading outside the frame, so the row read as three
         * panels and one loose title — the odd one out for no reason anybody
         * could name.
         */}
        <section className="home-card">
          <div className="home-card__head">
            <h2>Around Astrum</h2>
            <Link to="/feed" className="home__section-link">
              Feed
              <Icon name="arrow-right" size={12} />
            </Link>
          </div>
          {posts.length === 0 ? (
            <SectionEmpty icon="work" text="No company updates yet." />
          ) : (
            <ul className="home__posts">
              {posts.slice(0, 3).map((post) => (
                <li key={post.id}>
                  <Link to="/feed" className="home-post">
                    <Avatar name={post.authorName} src={post.authorAvatarUrl ?? undefined} size={30} />
                    <span className="home-post__body">
                      <span className="home-post__meta">
                        <strong>{post.authorName}</strong>
                        <span>{relativeTime(post.createdAt, now)}</span>
                      </span>
                      <span className="home-post__text">{post.body}</span>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>
    </PageBody>
  );
}
